import { html, TemplateResult } from "lit-html";
import {
  convertDateTime,
  convertMinutes,
  renderStringArray,
  convertFlightState,
  renderTerminal,
} from "Helper/flightInformationHelper";
import { I18nService } from "Services/I18nService";
import { FlightsInformation } from "Interfaces/flightsInformation";

/**
 * Template for flight table
 *
 * Renders JSON data from API w/ lit-html
 */

/** ####### HELPERS ####### */

/**
 * Helper, converts String (Minutes) into Hours and Minutes
 * @param {string} dateIn String
 * @param {string} labelHours String
 * @param {string} labelMinutes String
 * @returns {string}
 */
function convertHoursAndMinutes(dateIn: string, labelHours: string, labelMinutes: string): string {
  if (dateIn) {
    const time = convertMinutes(dateIn);
    return `${time[0]} ${labelHours} ${time[1]} ${labelMinutes}`;
  }
  return ``;
}

/**
 * Helper to show/hide row in list
 * @param {any} data
 * @returns {string}
 */
function hasData(data: any): string {
  if (data === undefined) {
    return `fra-h-hidden`;
  }
  return ``;
}

/** ####### RENDERER ####### */
/**
 * Render status, create coloured output
 *
 * @param {number} flstatus
 * @param {string} status
 * @param {string} label
 * @returns {TemplateResult}
 */
function renderStatus(flstatus: number, status: string, label: string): TemplateResult {
  return html`
    <div class="fra-e-list__entry fra-e-list__entry--state-${convertFlightState(flstatus)}">
      <dt class="fra-e-list__label">${label}</dt>
      <dd class="fra-e-list__desc">${status || "–"}</dd>
    </div>
  `;
}

/**
 * Render asterisk if airlineDelayUrl exists
 *
 * @param {string} airlineDelayUrl
 * @returns {TemplateResult}
 */
function renderAsterisk(airlineDelayUrl: string): TemplateResult {
  if (airlineDelayUrl !== "") {
    return html`
      <span class="fra-e-list__label--has-footnote">*</span>
    `;
  }

  return html``;
}

/**
 * Render asterisk if airlineDelayUrl exists
 *
 * @param {string} airlineDelayUrl
 * @returns {TemplateResult}
 */
function renderMobileAsterisk(airlineDelayUrl: string): TemplateResult {
  if (airlineDelayUrl !== "") {
    return html`
      <a
        class="fra-e-button fra-e-button--icon-only fra-e-list__desc-footnote fra-js-list__desc-footnote"
        href="#ref1"
        target="_self"
      >
        <fra-e-icon class="fra-e-icon fra-e-button__icon" icon-id="fra-icon-achtung-kreis"></fra-e-icon>
      </a>
    `;
  }

  return html``;
}

/**
 * Renders footnote if airlineDelayUrl exists
 *
 * @param {string} airlineDelayUrl
 * @param {string} delayLabel
 * @param {string} linkLabel
 * @returns {TemplateResult}
 */
function renderFootnote(airlineDelayUrl: string, delayLabel: string, linkLabel: string): TemplateResult {
  if (airlineDelayUrl !== "") {
    return html`
      <div class="fra-e-list__footnotes">
        <p class="fra-p--small fra-e-list__footnote-entry">
          ${delayLabel}
          <a href="${airlineDelayUrl}" class="fra-m-flight-detail__delay-link" target="_blank">${linkLabel}</a>
          .
        </p>
      </div>
      <fra-m-overlay class="fra-m-overlay fra-js-overlay fra-m-overlay--white">
        <div class="fra-grid">
          <div class="fra-grid-row">
            <div id="ref1" class="fra-e-list__footnotes fra-grid-col-mq1-12">
              <p class="fra-e-list__footnote-entry">
                ${delayLabel}
                <a href="${airlineDelayUrl}" class="fra-m-flight-detail__delay-link" target="_blank">${linkLabel}</a>
                .
              </p>
            </div>
          </div>
        </div>
      </fra-m-overlay>
    `;
  }

  return html``;
}

/** ####### EXPORTS ####### */

/**
 * Renders flight-detail-list
 *
 * @param {FlightsInformation} data
 * @param {string} airlineUrl
 * @param {string} airlineDelayUrl
 * @param {string} locale
 * @returns {TemplateResult}
 */
/* eslint-disable max-params */
export const flightsDetailTemplate = (
  data: FlightsInformation,
  airlineUrl: string,
  airlineDelayUrl: string,
  locale: string,
): TemplateResult => {
  return html`
    <fra-e-list class="fra-e-list">
      <dl class="fra-m-flight-detail__list">
        ${renderStatus(
          data.flstatus,
          data.status,
          I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.label"),
        )}
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.scheduled")}
            ${renderAsterisk(airlineDelayUrl)}
          </dt>
          <dd class="fra-e-list__desc ${hasData(data.sched)}">
            ${convertDateTime(data.sched, locale)} ${renderMobileAsterisk(airlineDelayUrl)}
          </dd>
        </div>
        ${
          data.schedArr
            ? html`
                <div class="fra-e-list__entry">
                  <dt class="fra-e-list__label">
                    ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.arrivaltime")}
                    ${renderAsterisk(airlineDelayUrl)}
                  </dt>
                  <dd class="fra-e-list__desc ${hasData(data.schedArr)}">
                    ${convertDateTime(data.schedArr, locale)} ${renderMobileAsterisk(airlineDelayUrl)}
                  </dd>
                </div>
              `
            : html``
        }            
        </div>
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.airline")}
          </dt>
          <dd class="fra-e-list__desc fra-e-list__desc-link ${hasData(data.alname)}">
            <a class="fra-e-link fra-e-link--with-icon fra-e-link--animated-right" href="${airlineUrl}" target="_self">
              <span class="fra-e-link__label">${data.alname}</span>
              <fra-e-icon class="fra-e-icon " icon-id="fra-icon-arrow-right"></fra-e-icon>
            </a>
          </dd>
        </div>
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.registration")}
          </dt>
          <dd class="fra-e-list__desc ${hasData(data.reg)}">
            ${data.reg}
          </dd>
        </div>
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.airplanetype")}
          </dt>
          <dd class="fra-e-list__desc ${hasData(data.ac)}">
            ${data.ac}
          </dd>
        </div>
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.flightnumber")}
          </dt>
          <dd class="fra-e-list__desc ${hasData(data.fnr)}">
            ${data.fnr}
          </dd>
        </div>
        ${
          data.cs
            ? html`
                <div class="fra-e-list__entry">
                  <dt class="fra-e-list__label">
                    ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.codeshare")}
                  </dt>
                  <dd class="fra-e-list__desc ${hasData(data.cs)}">${renderStringArray(data.cs)}</dd>
                </div>
              `
            : html``
        }        
        ${
          data.schalter
            ? html`
                <div class="fra-e-list__entry">
                  <dt class="fra-e-list__label">
                    ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.checkin")}
                  </dt>
                  <dd class="fra-e-list__desc ${hasData(data.schalter)}">${data.schalter}</dd>
                </div>
              `
            : html``
        }
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.terminal")},
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.hall")},
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.gate")}
          </dt>
          <dd class="fra-e-list__descy ${hasData(data.gate)}">
            ${renderTerminal(
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.terminal"),
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.hall"),
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.gate"),
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.exit"),
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.row.checkin"),
              data.terminal,
              data.halle,
              data.gate,
            )}
          </dd>
        </div>
        <div class="fra-e-list__entry">
          <dt class="fra-e-list__label">
            ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.flightduration")}
          </dt>
          <dd class="fra-e-list__desc ${hasData(data.duration)}">
            ${convertHoursAndMinutes(
              data.duration,
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.hours"),
              I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.minutes"),
            )}
          </dd>
        </div>
      </dl>
      <p class="fra-p--small">
        ${I18nService.getInstance().getKey("fraport.travel.components.content.flight.table.footnote")}
        ${convertDateTime(data.lu, locale)}
      </p>
      ${renderFootnote(
        airlineDelayUrl,
        I18nService.getInstance().getKey("fraport.travel.components.special.airline.delay.label"),
        I18nService.getInstance().getKey("fraport.travel.components.special.airline.delay.link.label"),
      )}
    </fra-e-list>
  `;
};
